/**
 * Admin Crashes API — grouped crash reports from the self-hosted
 * /v1/crash endpoint, surfaced via the admin console.
 *
 * Backend: GET /admin/crashes         → grouped list
 *          GET /admin/crashes/:hash    → recent samples for one group
 */

import { apiFetch } from "./client";

export type CrashPlatform =
  | "ios"
  | "ipados"
  | "android"
  | "macos"
  | "windows"
  | "admin";

export type CrashGroup = {
  readonly stack_hash: string;
  readonly error_name: string;
  readonly error_message: string;
  readonly platform: CrashPlatform;
  readonly count: number;
  readonly first_seen: string;
  readonly last_seen: string;
};

export type CrashSample = {
  readonly id: string;
  readonly error_name: string;
  readonly error_message: string;
  readonly stack: string;
  readonly breadcrumbs: unknown;
  readonly component_stack: string | null;
  readonly platform: CrashPlatform;
  readonly app_version: string | null;
  readonly os_version: string | null;
  readonly is_fatal: boolean;
  readonly device_id_hash: string | null;
  readonly user_id_hash: string | null;
  readonly extra: unknown;
  readonly client_timestamp: string;
  readonly created_at: string;
};

export async function fetchCrashGroups(params: {
  readonly platform?: CrashPlatform;
  readonly sinceHours: number;
  readonly limit?: number;
}): Promise<ReadonlyArray<CrashGroup>> {
  const qs = new URLSearchParams();
  qs.set("since_hours", String(params.sinceHours));
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.platform) qs.set("platform", params.platform);
  const res = await apiFetch<{ data: { groups: ReadonlyArray<CrashGroup> } }>(
    `/admin/crashes?${qs.toString()}`,
  );
  return res.data.groups;
}

export async function fetchCrashSamples(
  stackHash: string,
  limit = 20,
): Promise<ReadonlyArray<CrashSample>> {
  const res = await apiFetch<{
    data: { samples: ReadonlyArray<CrashSample> };
  }>(`/admin/crashes/${encodeURIComponent(stackHash)}?limit=${limit}`);
  return res.data.samples;
}
